/**
 * AttendeeResolver — fetches native attendees via expo-calendar's
 * getAttendeesForEventAsync() and maps them to EventParticipant[].
 *
 * This is the reliable read-path for event attendees. expo-calendar's
 * Event type does NOT include an `attendees` field reliably, but
 * getAttendeesForEventAsync() reads directly from CalendarContract.
 */

import * as Calendar from 'expo-calendar';
import { Platform } from 'react-native';
import type { EventParticipant } from '@/types/events';

export class AttendeeResolver {
  /**
   * Fetch native attendees for an event and map them to EventParticipant[].
   * Intended to be called lazily (e.g. when opening an event for editing).
   */
  static async resolveForEvent(
    eventId: string,
    organizer?: { email: string; name?: string | null } | null,
  ): Promise<EventParticipant[]> {
    try {
      const nativeAttendees = await Calendar.getAttendeesForEventAsync(eventId);

      if (!nativeAttendees || nativeAttendees.length === 0) {
        return [];
      }

      const organizerEmail = organizer?.email?.toLowerCase();

      return nativeAttendees
        .map(attendee => this.mapAttendeeToParticipant(attendee, organizerEmail))
        .filter((p): p is EventParticipant => p !== null);
    } catch (error) {
      console.error('[AttendeeResolver] Failed to resolve attendees:', error);
      return [];
    }
  }

  /**
   * Map a single native Calendar.Attendee to an EventParticipant.
   * Returns null if the attendee has no usable email.
   */
  private static mapAttendeeToParticipant(
    attendee: Calendar.Attendee,
    organizerEmail?: string,
  ): EventParticipant | null {
    const email = this.extractEmail(attendee);

    if (!email) {
      return null;
    }

    const isOrganizer = organizerEmail
      ? email.toLowerCase() === organizerEmail
      : false;

    return {
      id: `${isOrganizer ? 'organiser' : 'invited'}-${email}`,
      role: isOrganizer ? 'organiser' : 'invited',
      email,
      name: attendee.name ?? undefined,
      isSelf: false,
    };
  }

  /**
   * Extract email from an Attendee.
   * Android: uses the `email` field directly.
   * iOS: email may be in the `url` field as "mailto:user@example.com".
   */
  private static extractEmail(attendee: Calendar.Attendee): string | undefined {
    if (attendee.email) {
      return attendee.email;
    }

    // iOS stores email in the url field as "mailto:..."
    if (Platform.OS === 'ios' && (attendee as any).url) {
      const url = (attendee as any).url as string;
      if (url.startsWith('mailto:')) {
        return url.replace('mailto:', '');
      }
    }

    return undefined;
  }
}
